import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './JxSelect.css';

// Rough dropdown height budget (search input + list max-height + borders) — used only to decide
// open direction, not as an exact size. Flipping to bottom-anchored positioning lets the actual
// (possibly shorter, filtered) list grow upward naturally without needing an exact measurement.
const ESTIMATED_DROPDOWN_HEIGHT = 260;

/**
 * JxSelect — simpele dropdown met typeahead filter.
 * Eigen implementatie — geen MUI Autocomplete quirks.
 * Dropdown via createPortal op document.body — vrij van scroll container clipping.
 * Klapt automatisch naar boven open als er onderin de viewport te weinig ruimte is.
 *
 * @param {string} id
 * @param {number|string} value
 * @param {(value: number|string) => void} onChange
 * @param {Array<{label:string,value:number|string}>} options
 * @param {boolean} [disabled]
 */
export default function JxSelect({ id, value, onChange, options, disabled = false, defaultLabel = 'All Topics' }) {
    const selected   = options.find((o) => o.value === value) ?? null;
    const [open, setOpen]               = useState(false);
    const [query, setQuery]             = useState('');
    const [highlighted, setHighlighted] = useState(0);
    const [dropdownStyle, setDropdownStyle] = useState({});
    const inputRef = useRef(null);
    const listRef  = useRef(null);
    const rootRef  = useRef(null);

    const filtered = query.trim()
        ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
        : options;

    // Reset highlight when filtered list changes
    useEffect(() => { setHighlighted(0); }, [query]);

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const handle = (e) => {
            if (!rootRef.current?.contains(e.target)) close();
        };
        document.addEventListener('mousedown', handle);
        return () => document.removeEventListener('mousedown', handle);
    }, [open]);

    function openDropdown() {
        if (disabled) return;

        // Calculate portal position from trigger element — flip upward when there's not
        // enough room below but more room above (e.g. a row near the bottom of the viewport).
        const rect = rootRef.current?.getBoundingClientRect();
        if (rect) {
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            const openUpward = spaceBelow < ESTIMATED_DROPDOWN_HEIGHT && spaceAbove > spaceBelow;

            setDropdownStyle(
                openUpward
                    ? {
                          position: 'fixed',
                          bottom: window.innerHeight - rect.top + 4,
                          left: rect.left,
                          width: rect.width,
                          maxHeight: spaceAbove - 8,
                          zIndex: 10001,
                      }
                    : {
                          position: 'fixed',
                          top: rect.bottom + 4,
                          left: rect.left,
                          width: rect.width,
                          maxHeight: spaceBelow - 8,
                          zIndex: 10001,
                      }
            );
        }

        setQuery('');
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 0);
    }

    function close() {
        setOpen(false);
        setQuery('');
    }

    function select(option) {
        onChange(option.value);
        close();
    }

    function handleKeyDown(e) {
        if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted((h) => Math.min(h + 1, filtered.length - 1)); }
        if (e.key === 'ArrowUp')   { e.preventDefault(); setHighlighted((h) => Math.max(h - 1, 0)); }
        if (e.key === 'Enter' || e.key === 'Tab') {
            if (filtered[highlighted]) { e.preventDefault(); select(filtered[highlighted]); }
        }
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation(); // prevent JxForm global Escape-to-close from firing
            close();
        }
    }

    const dropdown = open && createPortal(
        <div className="jx-select-paper jx-select-dropdown" style={dropdownStyle}>
            <input
                ref={inputRef}
                className="jx-select-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search…"
            />
            <div ref={listRef} className="MuiAutocomplete-listbox">
                {filtered.length === 0 && (
                    <div className="jx-select-no-options">No options</div>
                )}
                {filtered.map((o, i) => (
                    <div
                        key={o.value}
                        className={[
                            'MuiAutocomplete-option',
                            o.value === value ? 'jx-select-option--selected' : '',
                            i === highlighted ? 'Mui-focused' : '',
                        ].filter(Boolean).join(' ')}
                        onMouseEnter={() => setHighlighted(i)}
                        onMouseDown={(e) => { e.preventDefault(); select(o); }}
                    >
                        {o.label}
                    </div>
                ))}
            </div>
        </div>,
        document.body
    );

    return (
        <div ref={rootRef} className="jx-select" id={id}>
            {/* Closed state — click to open */}
            <div
                className={`jx-select-value${open ? ' jx-select-value--open' : ''}`}
                onClick={openDropdown}
            >
                <span className="jx-select-label">{selected?.label ?? defaultLabel}</span>
                <span className="jx-select-arrow">{open ? '▲' : '▼'}</span>
            </div>

            {dropdown}
        </div>
    );
}
